// Library.tsx (updated with exact-ad scroll restore)
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import AdCard from "@/components/AdCard";
import LibraryAdGrid from "@/components/LibraryAdGrid";
import type { Ad } from "@/components/AdCard";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { usePricingModal } from "@/context/PricingModalContext";
import { useEntitlements } from "@/entitlements/useEntitlements";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, Search, SlidersHorizontal, HelpCircle } from "lucide-react";
import { FilterPanel, useFilterPanel } from "@/components/FilterPanel";
import { SortDropdown, SortOption, DEFAULT_SORT_OPTION } from "@/components/SortDropdown";
import { sortAds } from "@/lib/sortUtils";
import ObyectLoader from "@/components/ObyectLoader";
import { OnboardingModal, resetOnboarding } from "@/components/OnboardingModal";
import { getStorageUrl } from "@/lib/storage";
import { logger } from "@/lib/logger";
import "../index.css"; // for .btn-glow

interface AdWithTags extends Ad {
  tagNames?: string[];
  requiresSubscription?: boolean;
  createdAt: string;
  imagePath?: string;
  is_black_friday?: boolean;
  brandLogoUrl?: string;
  __seq?: number;
  is_active_on_facebook?: boolean | null;
  days_running?: number | null;
}

const PAGE_SIZE = 20;
const EAGER_FIRST_N = 20;
const FREE_PAGE_SIZE = 20;
const LIBRARY_STATE_KEY = "obyect_library_state_v2";
const LIB_LAST_AD_KEY = "obyect_library_last_ad_id";

function preloadImages(urls: string[], timeout = 15000): Promise<void> {
  const load = (url: string) =>
    new Promise<void>((resolve) => {
      const img = new Image();
      const cleanup = () => {
        // Clear references to allow garbage collection
        img.onload = null;
        img.onerror = null;
        img.src = "";
        resolve();
      };
      img.onload = cleanup;
      img.onerror = cleanup;
      img.src = url;
      setTimeout(cleanup, timeout);
    });
  return Promise.all(urls.map(load)).then(() => undefined);
}

function useDebounced<T>(value: T, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const Library = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Filters
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [timespan, setTimespan] = useState<"week" | "month" | "year" | "all">("all");
  const [brands, setBrands] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const debouncedSearch = useDebounced(searchTerm, 250);

  // Filter Panel state
  const {
    open: filterPanelOpen,
    setOpen: setFilterPanelOpen,
    filters: panelFilters,
    setFilters: setPanelFilters,
    handleApply: handleFilterApply,
    handleClear: handleFilterClear,
    activeFilterCount,
  } = useFilterPanel();

  // Sort state
  const [sortOption, setSortOption] = useState<SortOption>(DEFAULT_SORT_OPTION);
  const [shuffleKey, setShuffleKey] = useState(0);

  // Data
  const [adList, setAdList] = useState<AdWithTags[]>([]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);

  // name -> id maps
  const [brandMap, setBrandMap] = useState<Record<string, string>>({});
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});

  // Auth / access
  const { openPricingModal: openPricingModalFromContext } = usePricingModal();
  const { isAuthenticated, hasValidAccess, entitlementLoaded } = useEntitlements();

  // ⚛️ React DevTools Profiling: AdCard Memoization Optimization
  // Store latest context function in ref to avoid breaking memoization.
  // The context function isn't wrapped in useCallback, so it creates a new reference on every render.
  // By using a ref + stable callback wrapper, we ensure AdCard receives the same onUpgradeClick
  // prop reference across renders, preventing unnecessary re-renders when verified with React DevTools Profiler.
  const openPricingModalRef = useRef(openPricingModalFromContext);
  useEffect(() => {
    openPricingModalRef.current = openPricingModalFromContext;
  }, [openPricingModalFromContext]);

  // ⚛️ Stable callback for AdCard memoization
  // This wrapper has an empty dependency array, ensuring it never changes.
  // Without this, every parent re-render would break AdCard's React.memo() optimization.
  const openPricingModal = useCallback(() => {
    openPricingModalRef.current();
  }, []);

  // Auth modal for guests (auto-open on /library)
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Loading flags
  const [adsLoading, setAdsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Black Friday modal
  const [blackFridayModalOpen, setBlackFridayModalOpen] = useState(false);
  const [hasBlackFridayAccess, setHasBlackFridayAccess] = useState(false);
  const canBypassBlur = hasValidAccess || hasBlackFridayAccess;
  const canBypassBlurNow = canBypassBlur; // readability

  // Onboarding modal (for help button re-trigger)
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Mobile toolbar show/hide on scroll
  const [toolbarVisible, setToolbarVisible] = useState(true);
  const lastScrollYRef = useRef(0);

  const isFetchingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const adsLoadingRef = useRef(true);
  useEffect(() => { isFetchingMoreRef.current = isFetchingMore; }, [isFetchingMore]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { adsLoadingRef.current = adsLoading; }, [adsLoading]);

  // Pagination + restore
  const offsetRef = useRef(0);
  const restoredFromSessionRef = useRef(false);
  const savedScrollYRef = useRef(0);
  const pendingScrollRestoreRef = useRef(false);

  useEffect(() => {
  const state = location.state as any;

  if (state?.resetFilters) {
    // 🔹 1) Clear ALL filter state here
    setBrands([]);
    setCategories([]);
    // Clear panel filters
    handleFilterClear();
    // reset sort
    setSortOption(DEFAULT_SORT_OPTION);

    // 🔹 2) If you store filters in localStorage, clear that too
    // localStorage.removeItem("obyect_library_filters");

    // 🔹 3) Remove the reset flag from history so it doesn't re-trigger
    navigate(location.pathname, { replace: true, state: {} });
  }
}, [location.state, navigate, location.pathname, handleFilterClear]);


  useEffect(() => {
    const KEY = "obyect_library_login_prompt_v1";
    const already = typeof window !== "undefined" && sessionStorage.getItem(KEY) === "1";
    if (entitlementLoaded && !isAuthenticated && !already) {
      setLoginModalOpen(true);
      try { sessionStorage.setItem(KEY, "1"); } catch {/* noop */}
    }
  }, [entitlementLoaded, isAuthenticated]);

  useEffect(() => {
    document.title = "Ad Library - Filters and Discovery";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Browse the full ads library with filters for brand and category.");
  }, []);

  // URL → brands/categories
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const brandParam = params.get("brand");
    const categoryParam = params.get("category");

    if (brandParam && brandParam !== "all") {
      setBrands(brandParam.split(",").map((b) => b.trim()).filter(Boolean));
    }

    if (categoryParam && categoryParam !== "all") {
      setCategories(categoryParam.split(",").map((c) => c.trim()).filter(Boolean));
    }
  }, [location.search]);

  // load maps + options
  useEffect(() => {
    (async () => {
      try {
        const [b, c, t] = await Promise.all([
          supabase.from("brands").select("id,name"),
          supabase.from("categories").select("id,name"),
          supabase.from("tags").select("id,name"),
        ]);
        const bMap: Record<string, string> = {};
        const cMap: Record<string, string> = {};
        (b.data || []).forEach((r: any) => { bMap[r.name] = r.id; });
        (c.data || []).forEach((r: any) => { cMap[r.name] = r.id; });
        setBrandMap(bMap);
        setCategoryMap(cMap);
        setBrandOptions((b.data || []).map((r: any) => r.name).sort());
        setCategoryOptions((c.data || []).map((r: any) => r.name).sort());
        setTagOptions((t.data || []).map((r: any) => r.name).sort());
      } catch { /* noop */ }
    })();
  }, []);

  type FilterSig = {
    brandIds: string[];
    categoryIds: string[];
    cutoffISO?: string;
  };

  const filterSig = useMemo<FilterSig>(() => {
    const brandIds = brands
      .map((name) => brandMap[name])
      .filter(Boolean);

    const categoryIds = categories
      .map((name) => categoryMap[name])
      .filter(Boolean);

    let cutoffISO: string | undefined;
    if (timespan !== "all") {
      const now = new Date();
      if (timespan === "week") now.setDate(now.getDate() - 7);
      if (timespan === "month") now.setMonth(now.getMonth() - 1);
      if (timespan === "year") now.setFullYear(now.getFullYear() - 1);
      cutoffISO = now.toISOString();
    }

    return { brandIds, categoryIds, cutoffISO };
  }, [brands, categories, brandMap, categoryMap, timespan]);

    const filtersKey = useMemo(
    () =>
      JSON.stringify({
        brandIds: filterSig.brandIds,
        categoryIds: filterSig.categoryIds,
        cutoffISO: filterSig.cutoffISO ?? null,
      }),
    [filterSig]
  );

  const latestFiltersKeyRef = useRef(filtersKey);
  useEffect(() => {
    latestFiltersKeyRef.current = filtersKey;
  }, [filtersKey]);


  /** Page fetcher (non-BF only) */
  const fetchPage = useCallback(async (reset = false) => {
    if (isFetchingMoreRef.current) return;
    setIsFetchingMore(true);
    isFetchingMoreRef.current = true;
    const requestFiltersKey = filtersKey;

    if (reset) {
      setAdsLoading(true);
      adsLoadingRef.current = true;
      offsetRef.current = 0;
      setHasMore(true);
      hasMoreRef.current = true;
    }

    try {
      const rangeFrom = offsetRef.current;
      const rangeTo = rangeFrom + PAGE_SIZE - 1;

      let q = supabase
        .from("ads_with_bf" as any)
        .select(`
          id,
          image_path,
          brand_id,
          category_id,
          created_at,
          requires_subscription,
          is_black_friday,
          is_active_on_facebook,
          days_running,
          brand:brands(name,logo_path),
          category:categories(name),
          ad_tags:ad_tags(tags(name))
        `)
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .range(rangeFrom, rangeTo);

      // filters
      if (filterSig.brandIds.length === 1) {
        q = q.eq("brand_id", filterSig.brandIds[0]);
      } else if (filterSig.brandIds.length > 1) {
        q = q.in("brand_id", filterSig.brandIds);
      }

      if (filterSig.categoryIds.length === 1) {
        q = q.eq("category_id", filterSig.categoryIds[0]);
      } else if (filterSig.categoryIds.length > 1) {
        q = q.in("category_id", filterSig.categoryIds);
      }

      if (filterSig.cutoffISO) {
        q = q.gte("created_at", filterSig.cutoffISO);
      }

      // library: NON-BF only
      q = q.eq("is_black_friday", false);

      // Only show live ads
      q = q.eq("is_live", true);

      const { data: rows, error } = await q;
      if (error) throw error;

      // ❌ If filters changed while this request was in-flight, ignore result
      if (requestFiltersKey !== latestFiltersKeyRef.current) {
        // Do NOT touch adList / hasMore / offset, but DO reset the fetching flag
        setIsFetchingMore(false);
        isFetchingMoreRef.current = false;
        return;
      }

      const from = offsetRef.current;

      let mapped = (rows ?? [])
        // Filter out ads from brands without logo (missing identity)
        .filter((row: any) => {
          const brandLogoPath = row?.brand?.logo_path ?? row?.brand_logo_path;
          return !!brandLogoPath;
        })
        .map((row: any) => {
          const imageUrl = getStorageUrl("ad-images", row.image_path) ?? "";

          let brandLogoUrl: string | undefined;
          const brandLogoPath = row?.brand?.logo_path ?? row?.brand_logo_path;
          if (brandLogoPath) {
            brandLogoUrl = getStorageUrl("brand-logos", brandLogoPath);
          }

          const brandName = row?.brand?.name ?? row?.brand_name ?? "Unknown brand";
          const categoryName = row?.category?.name ?? row?.category_name ?? "Unknown category";

          const tagNames: string[] = Array.isArray(row?.ad_tags)
            ? row.ad_tags.map((t: any) => t?.tags?.name).filter(Boolean)
            : Array.isArray(row?.tag_names)
              ? row.tag_names.filter(Boolean)
              : [];

          return {
            id: String(row.id),
            brand: brandName,
            category: categoryName,
            image: imageUrl,
            imagePath: row.image_path,
            brandLogoUrl,
            copy: "",
            start: new Date().toISOString(),
            end: new Date().toISOString(),
            ctr: 0,
            conversions: 0,
            engagement: 0,
            createdAt: row.created_at,
            tagNames,
            requiresSubscription: !!row.requires_subscription,
            is_black_friday: !!row.is_black_friday,
            is_active_on_facebook: row.is_active_on_facebook ?? null,
            days_running: row.days_running ?? null,
          } as AdWithTags;
        });

      // assign stable seq (no shuffle here - handled by sortAds)
      mapped = mapped.map((ad, i) => ({
        ...ad,
        __seq: from + i,
      }));

      // preload images
      await preloadImages(mapped.map((m) => m.image));

      setAdList((prev) => (reset ? mapped : [...prev, ...mapped]));

      if (!rows || rows.length === 0) {
        setHasMore(false); hasMoreRef.current = false;
      } else {
        offsetRef.current += rows.length;
        if (rows.length < PAGE_SIZE) {
          setHasMore(false); hasMoreRef.current = false;
        }
      }
    } catch (e: any) {
      logger.error("Failed to load library ads", e);
      toast({
        title: "Load failed",
        description: e?.message ?? "Could not load more ads.",
        variant: "destructive" as any
      });
    } finally {
      setIsFetchingMore(false);
      isFetchingMoreRef.current = false;
      if (reset) {
        setAdsLoading(false);
        adsLoadingRef.current = false;
      }
    }
  }, [filterSig, filtersKey]);



  // banner
  useEffect(() => {
    (async () => {
      try {
        const { data: bRow } = await (supabase as any)
          .from("page_banners")
          .select("image_path")
          .eq("page", "library")
          .single();
        if (bRow?.image_path) {
          const pubUrl = getStorageUrl("banners", bRow.image_path);
          setBannerUrl(pubUrl ?? null);
        } else setBannerUrl(null);
      } catch {/* noop */}
    })();
  }, []);

  // sentinel → fetchPage
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const onIntersect: IntersectionObserverCallback = ([first]) => {
      if (!first.isIntersecting) return;
      if (!hasMoreRef.current) return;
      if (isFetchingMoreRef.current) return;
      if (adsLoadingRef.current) return;
      fetchPage(false);
    };
    const io = new IntersectionObserver(onIntersect, {
      root: null,
      rootMargin: "1600px 0px 1600px 0px",
      threshold: 0,
    });
    io.observe(el);
    return () => io.disconnect();
  }, [fetchPage]);

  // Restore from session
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const raw = sessionStorage.getItem(LIBRARY_STATE_KEY);
      if (!raw) return;

      const saved = JSON.parse(raw);

      if (Array.isArray(saved.adList) && saved.adList.length > 0) {
        // Check if URL has filter parameters - if so, don't restore from session
        const params = new URLSearchParams(location.search);
        const hasBrandParam = params.has("brand") && params.get("brand") !== "all";
        const hasCategoryParam = params.has("category") && params.get("category") !== "all";

        // If URL has filters, skip restoration entirely to force fresh fetch
        if (hasBrandParam || hasCategoryParam) {
          return;
        }

        // Only restore if no URL filters
        setAdList(saved.adList);
        setBrands(saved.brands || []);
        setCategories(saved.categories || []);
        setTimespan(saved.timespan || "all");
        setSearchTerm(saved.searchTerm || "");
        // Restore sort option
        if (saved.sortOption) {
          setSortOption(saved.sortOption);
        }
        // Restore panel filters
        if (saved.panelFilters) {
          setPanelFilters(saved.panelFilters);
        }

        offsetRef.current = saved.offset ?? saved.adList.length ?? 0;
        // Always allow fetching more - don't restore hasMore: false
        setHasMore(true);
        hasMoreRef.current = true;

        adsLoadingRef.current = false;
        setAdsLoading(false);

        restoredFromSessionRef.current = true;

        const scrollY = typeof saved.scrollY === "number" ? saved.scrollY : 0;
        savedScrollYRef.current = scrollY;
        pendingScrollRestoreRef.current = true;
      }
    } catch (err) {
      logger.warn("Failed to restore library state", err);
    }
  }, []);

  // manual scroll restoration
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.history.scrollRestoration = "manual";
    } catch {
      // ignore
    }
  }, []);

  // After data is ready, restore scroll (exact ad first, then scrollY)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!pendingScrollRestoreRef.current) return;
    if (!entitlementLoaded) return;
    if (adsLoading) return;
    if (!adList.length) return;

    pendingScrollRestoreRef.current = false;

    const ALIGN_OFFSET = 120; // px to keep the ad nicely in view (tweak if you want)

    const scrollToLastAd = () => {
      let didScroll = false;

      try {
        const lastAdId = sessionStorage.getItem(LIB_LAST_AD_KEY);
        if (lastAdId) {
          const el = document.querySelector<HTMLElement>(
            `[data-library-ad-id="${lastAdId}"]`
          );
          if (el) {
            const rect = el.getBoundingClientRect();
            const absoluteTop = window.scrollY + rect.top;
            const targetTop = absoluteTop - ALIGN_OFFSET;

            window.scrollTo({
              top: targetTop < 0 ? 0 : targetTop,
              behavior: "auto",
            });

            didScroll = true;
          }
        }
      } catch {
        // ignore
      }

      return didScroll;
    };

    requestAnimationFrame(() => {
      const didScrollToAd = scrollToLastAd();

      // 🔁 If layout shifts a bit after images settle, align again once
      if (didScrollToAd) {
        setTimeout(() => {
          scrollToLastAd();
        }, 250);
      }

      if (!didScrollToAd) {
        window.scrollTo({
          top: savedScrollYRef.current || 0,
          behavior: "auto",
        });
      }
    });
  }, [entitlementLoaded, adsLoading, adList.length]);


  // Load ads after entitlements + on filter change
  useEffect(() => {
    if (!entitlementLoaded) return;

    if (restoredFromSessionRef.current) {
      restoredFromSessionRef.current = false;
      return;
    }

    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitlementLoaded, filterSig]);

  // Persist state (limited to prevent memory bloat)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!adList.length) return;

    try {
      const raw = sessionStorage.getItem(LIBRARY_STATE_KEY);
      const prev = raw ? JSON.parse(raw) : {};

      // Only store first 100 ads with minimal fields to prevent memory bloat
      // Include createdAt for sort restoration
      const minimalAdList = adList.slice(0, 100).map(({ id, brand, category, image, brandLogoUrl, createdAt, __seq }) => ({
        id, brand, category, image, brandLogoUrl, createdAt, __seq
      }));

      const toSave = {
        ...prev,
        adList: minimalAdList,
        brands,
        categories,
        timespan,
        searchTerm,
        sortOption,
        panelFilters,
        offset: offsetRef.current,
        hasMore,
      };

      sessionStorage.setItem(LIBRARY_STATE_KEY, JSON.stringify(toSave));
    } catch (err) {
      logger.warn("Failed to persist library state", err);
    }
  }, [adList, brands, categories, timespan, searchTerm, sortOption, panelFilters, hasMore]);


  // Track scrollY + toolbar visibility on scroll direction
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Save scrollY to session storage
      if (adList.length) {
        try {
          const raw = sessionStorage.getItem(LIBRARY_STATE_KEY);
          const prev = raw ? JSON.parse(raw) : {};
          prev.scrollY = currentScrollY;
          sessionStorage.setItem(LIBRARY_STATE_KEY, JSON.stringify(prev));
        } catch {
          // ignore
        }
      }

      // Toolbar show/hide logic
      const scrollDelta = currentScrollY - lastScrollYRef.current;

      // Near top of page → always show
      if (currentScrollY < 100) {
        setToolbarVisible(true);
      }
      // Scrolling down significantly → hide
      else if (scrollDelta > 15) {
        setToolbarVisible(false);
      }
      // Scrolling up significantly → show
      else if (scrollDelta < -15) {
        setToolbarVisible(true);
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [adList.length]);

  const handleSortChange = useCallback((newSort: SortOption) => {
    if (newSort === "shuffle") {
      setShuffleKey(k => k + 1); // force re-shuffle even if already on shuffle
    }
    setSortOption(newSort);
  }, []);

  const filtered = useMemo(() => {
    let data = [...adList];

    // 🔹 Filter by selected brands (client-side) - from URL params or legacy state
    if (brands.length) {
      const brandSet = new Set(brands.map((b) => b.toLowerCase()));
      data = data.filter((a) => brandSet.has(a.brand.toLowerCase()));
    }

    // 🔹 Filter by selected categories (client-side) - from URL params or legacy state
    if (categories.length) {
      const categorySet = new Set(categories.map((c) => c.toLowerCase()));
      data = data.filter((a) => categorySet.has(a.category.toLowerCase()));
    }

    // 🔹 Filter by industries from FilterPanel (maps to categories)
    if (panelFilters.industries.length) {
      const industrySet = new Set(panelFilters.industries.map((i) => i.toLowerCase()));
      data = data.filter((a) => industrySet.has(a.category.toLowerCase()));
    }

    // 🔹 Filter by search term
    if (debouncedSearch.trim()) {
      const term = debouncedSearch.toLowerCase().trim();
      data = data.filter(
        (a) =>
          a.brand.toLowerCase().includes(term) ||
          a.category.toLowerCase().includes(term)
      );
    }

    // 🔹 Filter by topics from FilterPanel (maps to tags)
    if (panelFilters.topics.length) {
      const topicSet = new Set(panelFilters.topics.map((t) => t.toLowerCase()));
      data = data.filter((a) =>
        a.tagNames?.some((tag) => topicSet.has(tag.toLowerCase()))
      );
    }

    // 🔹 Apply sort based on selected option
    data = sortAds(data, sortOption);

    return data;
  }, [adList, brands, categories, debouncedSearch, sortOption, panelFilters, shuffleKey]);


  const hasFiltersApplied = useMemo(
    () =>
      debouncedSearch.trim() !== "" ||
      timespan !== "all" ||
      brands.length > 0 ||
      categories.length > 0 ||
      activeFilterCount > 0,
    [debouncedSearch, timespan, brands, categories, activeFilterCount]
  );

  const stateKey = useMemo(
    () =>
      [
        "all",
        brands.slice().sort().join(","),
        categories.slice().sort().join(","),
        timespan,
        (debouncedSearch || "").trim().toLowerCase(),
      ].join("|"),
    [brands, categories, timespan, debouncedSearch]
  );

  // ⚛️ Stable callback for AdCard memoization
  // AdCard is wrapped in React.memo(), so it only re-renders when props change.
  // This callback must be wrapped in useCallback with empty deps to maintain a stable reference.
  // Without this, AdCard would re-render on every parent update (visible in React DevTools Profiler).
  const handleAdDelete = useCallback(() => {
    // Empty function - delete not implemented in Library view
  }, []);

  // Loader
  if (!entitlementLoaded || adsLoading) {
    const PLACEHOLDERS = 12;

    return (
      <div className="min-h-screen bg-background relative">
        <main className="mx-auto w-full py-8 animation-fade-in">
          {/* Search/Filter Skeleton - Matches Floating Glass Toolbar */}
          <section className="sticky top-4 z-40 mb-8 mx-auto max-w-4xl px-3 md:px-6">
             <div className="h-[60px] w-full bg-[#13171c] border border-white/5 rounded-[24px] shadow-2xl animate-pulse" />
          </section>

          {/* Grid Skeleton - Matches LibraryAdGrid's CSS Grid structure exactly */}
          <section>
            <div className="mx-auto w-full px-3 md:px-6">
              <div className="
                grid gap-2.5 md:gap-4
                grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 min-[2000px]:grid-cols-5 min-[3000px]:grid-cols-6
              ">
                {Array.from({ length: PLACEHOLDERS }).map((_, i) => (
                  <div key={`ph-${i}`} className="flex flex-col gap-2.5">
                    <div className="rounded-[16px] overflow-hidden bg-[#13171c] border-0 ring-1 ring-white/5 shadow-2xl inner-glow">
                      {/* Header: Avatar + Text */}
                      <div className="p-4 flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-white/10 animate-pulse shrink-0" />
                        <div className="space-y-1.5 flex-1">
                          <div className="h-3.5 w-24 bg-white/10 animate-pulse rounded" />
                          <div className="h-2.5 w-16 bg-white/5 animate-pulse rounded" />
                        </div>
                      </div>
                      {/* Media: Image */}
                      <div className="px-3 pb-3">
                        <div className="aspect-[4/5] w-full bg-white/5 animate-pulse rounded-[8px]" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <main className="mx-auto w-full py-8 animate-fade-in">
        {/* Filter Bar - Floating Glass Toolbar */}
        <section className={`
          sticky top-2 md:top-4 z-40 mb-6 md:mb-8 mx-auto max-w-4xl px-2 md:px-6
          transition-all duration-300 ease-out
          ${toolbarVisible ? 'translate-y-0 opacity-100' : '-translate-y-[120%] opacity-0 pointer-events-none'}
        `}>
          <div className="
            flex flex-col md:flex-row items-center gap-1.5 md:gap-2 p-1.5 md:p-2 rounded-[16px] md:rounded-[24px]
            bg-[#13171c]/90 md:bg-[#13171c]/80 backdrop-blur-xl border border-white/10 shadow-2xl
            ring-1 ring-white/5
          ">
            {/* Search - Integrated into the bar */}
            <div className="relative flex-1 w-full md:w-auto">
              <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                type="text"
                placeholder="Search brands & categories…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="
                  pl-9 md:pl-10 h-9 md:h-11 w-full text-sm md:text-base
                  bg-transparent border-0 ring-0 focus-visible:ring-0
                  text-white placeholder:text-white/30
                "
              />
            </div>

            {/* Divider for desktop */}
            <div className="hidden md:block w-px h-8 bg-white/10" />

            {/* Filters & Sort - Horizontal on desktop */}
            <div className="flex items-center gap-2 w-full md:w-auto">
              {/* Filter Panel Trigger Button */}
              <button
                type="button"
                onClick={() => setFilterPanelOpen(true)}
                className="
                  inline-flex items-center justify-center gap-2
                  h-10 px-4 rounded-md
                  border border-gray-300
                  bg-background text-sm font-medium
                  hover:bg-accent
                  focus:outline-none focus:ring-0 focus:border-gray-300
                  active:outline-none active:ring-0
                  transition-colors
                "
              >
                <SlidersHorizontal className="h-4 w-4 shrink-0" />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-primary/20 text-primary rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Sort Dropdown */}
              <div className="flex-1 md:flex-initial min-w-[140px]">
                <SortDropdown
                  value={sortOption}
                  onChange={handleSortChange}
                  onReshuffle={() => setShuffleKey(k => k + 1)}
                  disabled={adsLoading}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Grid */}
        <LibraryAdGrid
          ads={filtered}
          pageSizeFree={FREE_PAGE_SIZE}
          hasFiltersApplied={hasFiltersApplied}
          adsLoading={adsLoading && adList.length === 0}
          bypassSubscriptionCheck={false}
          stateKey={stateKey}
          layoutMode="masonry"
          preserveOrder
          initialRenderCount={80}
          pageIncrement={40}
          forceEagerFirstN={40}
          entitlements={{ isAuthenticated, hasValidAccess, entitlementLoaded }}
          hasMoreFromServer={hasMore}
          onRequestMore={() => fetchPage(false)}
          onRequireAuth={() => {
            const next = encodeURIComponent(`${location.pathname}${location.search}`);
            navigate(`/auth/signin?next=${next}`);
          }}
          onRequireSubscribe={() => { openPricingModal(); }}
          renderItem={(ad, shouldBlur, { eager }) => {
            const handleClick = () => {
              try {
                sessionStorage.setItem(LIB_LAST_AD_KEY, ad.id.toString());
              } catch {
                // ignore
              }
            };

            return (
              <div
                data-library-ad-id={ad.id}
                onClick={handleClick}
                className="scroll-mt-12" // ~8rem margin from top when scrolled into view
              >
                <AdCard
                  key={ad.id}
                  ad={ad}
                  showDelete={isAuthenticated}
                  onDelete={handleAdDelete}
                  showBlur={!!shouldBlur}
                  onUpgradeClick={openPricingModal}
                  eager={!!eager}
                  stateStorageKey={LIBRARY_STATE_KEY}
                />
              </div>
            );
          }}
          emptyState={
            <div className="text-center py-12 text-muted-foreground">
              <p>No ads found matching your filters.</p>
            </div>
          }
        />

        {hasMore && <div ref={sentinelRef} className="h-20 w-full" />}
        {!hasMore && adList.length > 0 && (
          <div className="text-center text-xs text-muted-foreground py-6">
            You've reached the end.
          </div>
        )}
      </main>

      {/* Keep your success modal only (optional) */}
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent className="overflow-hidden p-0">
          <div className="relative">
            <div className="h-24 bg-[radial-gradient(280%_160%_at_0%_10%,rgba(118,91,230,0.16),rgba(208,136,255,0.08)_40%,transparent_70%)]" />
          </div>
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Payment Successful
              </DialogTitle>
              <DialogDescription>Thanks! Your purchase was successful. You now have full access.</DialogDescription>
            </DialogHeader>
          </div>
        </DialogContent>
      </Dialog>

      {/* Logged-out CTA */}
      <Dialog open={blackFridayModalOpen} onOpenChange={setBlackFridayModalOpen}>
        <DialogContent className="overflow-hidden p-0 max-w-md">
          <div className="relative">
            <div className="h-24 bg-[radial-gradient(280%_160%_at_0%_10%,rgba(255,215,0,0.16),rgba(255,165,0,0.08)_40%,transparent_70%)]" />
            <div className="absolute left-1/2 top-6 -translate-x-1/2">
              <span className="px-4 py-1 rounded-full text-sm font-medium bg-[radial-gradient(120%_280%_at_0%_10%,rgba(255,215,0,0.16),rgba(255,165,0,0.08)_80%,transparent_70%)] backdrop-blur border border-white/40 shadow-sm">
                🪄 Exclusive Access
              </span>
            </div>
          </div>

          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                🤑 Black Friday Collection
              </DialogTitle>
              <DialogDescription className="text-base mt-2">
                We collected all the top performing Black Friday ads in one place-handpicked by top creative strategists.
                <br />
                <br />
                Create an <strong>account</strong> for <strong>free access</strong> to this library.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setBlackFridayModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => { setBlackFridayModalOpen(false); navigate("/auth/signup"); }} className="bg-primary hover:bg-primary/90">
                Get access now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating Help Button */}
      <button
        onClick={() => {
          resetOnboarding();
          setShowOnboarding(true);
        }}
        className="fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full bg-primary/90 text-white shadow-lg hover:bg-primary hover:scale-105 transition-all flex items-center justify-center"
        aria-label="Help & Shortcuts"
        title="Help & Shortcuts"
      >
        <HelpCircle className="h-5 w-5" />
      </button>

      {/* Onboarding - shows on first visit or when help button clicked */}
      <OnboardingModal
        forceOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
      />

      {/* Filter Panel */}
      <FilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        filters={panelFilters}
        industryOptions={categoryOptions}
        topicOptions={tagOptions}
        onApply={handleFilterApply}
        onClear={handleFilterClear}
      />
    </div>
  );
};

export default Library;
